import { useReactFlow } from '@xyflow/react';
import { Leva, useControls, button } from 'leva';
import { useCallback, useRef } from 'react';
import layoutAlgorithms, {
  type Direction,
  type LayoutAlgorithmOptions,
} from '../auto-layout/algorithms';
import { useWorkflow } from '../context/WorkflowContext';
import { WorkflowHandlerID } from '../../workflow/ui';

type LayoutOptions = {
  algorithm: keyof typeof layoutAlgorithms;
} & LayoutAlgorithmOptions;

const HANDLE_REMAP_TO_LR: Record<string, string> = {
  [WorkflowHandlerID.SOURCE_BOTTOM]: WorkflowHandlerID.SOURCE_RIGHT,
  [WorkflowHandlerID.TARGET_TOP]: WorkflowHandlerID.TARGET_LEFT,
  [WorkflowHandlerID.SOURCE_BOTTOM_TRUE]: WorkflowHandlerID.SOURCE_RIGHT_TRUE,
  [WorkflowHandlerID.SOURCE_BOTTOM_FALSE]: WorkflowHandlerID.SOURCE_RIGHT_FALSE,
  [WorkflowHandlerID.SOURCE_TOP]: WorkflowHandlerID.SOURCE_RIGHT,
  [WorkflowHandlerID.TARGET_BOTTOM]: WorkflowHandlerID.TARGET_LEFT,
};

const HANDLE_REMAP_TO_TB: Record<string, string> = {
  [WorkflowHandlerID.SOURCE_RIGHT]: WorkflowHandlerID.SOURCE_BOTTOM,
  [WorkflowHandlerID.TARGET_LEFT]: WorkflowHandlerID.TARGET_TOP,
  [WorkflowHandlerID.SOURCE_RIGHT_TRUE]: WorkflowHandlerID.SOURCE_BOTTOM_TRUE,
  [WorkflowHandlerID.SOURCE_RIGHT_FALSE]: WorkflowHandlerID.SOURCE_BOTTOM_FALSE,
  [WorkflowHandlerID.SOURCE_TOP]: WorkflowHandlerID.SOURCE_BOTTOM,
  [WorkflowHandlerID.TARGET_BOTTOM]: WorkflowHandlerID.TARGET_TOP,
};

function remapHandleId(
  handleId: string | null | undefined,
  direction: Direction
): string | null | undefined {
  if (!handleId) return handleId;
  if (direction === 'LR') return HANDLE_REMAP_TO_LR[handleId] ?? handleId;
  if (direction === 'TB') return HANDLE_REMAP_TO_TB[handleId] ?? handleId;
  return handleId;
}

export const AutoLayoutPanel = ({ isVisible }: { isVisible: boolean }) => {
  const { getNodes, getEdges, fitView } = useReactFlow();
  const { workflow, setWorkflow, saveCheckpoint } = useWorkflow();
  const isRunning = useRef(false);

  const applyLayout = useCallback(
    async (options: LayoutOptions) => {
      if (isRunning.current || !workflow) return;
      isRunning.current = true;

      try {
        const currentNodes = getNodes().map((node) => ({ ...node }));
        const currentEdges = getEdges().map((edge) => ({ ...edge }));

        if (currentNodes.length === 0) return;

        const layoutAlgorithm = layoutAlgorithms[options.algorithm];
        const { nodes: layoutedNodes } = await layoutAlgorithm(
          currentNodes,
          currentEdges,
          options
        );

        const positionMap = new Map<string, { x: number; y: number }>();
        for (const node of layoutedNodes) {
          positionMap.set(node.id, node.position);
        }

        saveCheckpoint();

        const updatedWorkflow = {
          ...workflow,
          steps: workflow.steps.map((step) => {
            const newPos = positionMap.get(step.id);
            if (newPos) {
              return {
                ...step,
                ui: { ...step.ui, reactFlow: { ...step.ui?.reactFlow, position: newPos } },
              };
            }
            return step;
          }),
          triggers: workflow.triggers?.map((trigger) => {
            const newPos = positionMap.get(trigger.id);
            if (newPos) {
              return {
                ...trigger,
                ui: { ...trigger.ui, reactFlow: { ...trigger.ui?.reactFlow, position: newPos } },
              };
            }
            return trigger;
          }),
          stepTransitions: workflow.stepTransitions.map((t) => ({
            ...t,
            ui: {
              ...t.ui,
              reactFlow: {
                ...t.ui?.reactFlow,
                handle: {
                  sourceHandle: remapHandleId(
                    t.ui?.reactFlow?.handle?.sourceHandle,
                    options.direction
                  ),
                  targetHandle: remapHandleId(
                    t.ui?.reactFlow?.handle?.targetHandle,
                    options.direction
                  ),
                },
              },
            },
          })),
        };

        setWorkflow(updatedWorkflow);

        setTimeout(() => fitView({ duration: 300 }), 100);
      } finally {
        isRunning.current = false;
      }
    },
    [getNodes, getEdges, workflow, setWorkflow, saveCheckpoint, fitView]
  );

  const optionsRef = useRef<LayoutOptions>({
    algorithm: 'dagre',
    direction: 'LR',
    spacing: [50, 50],
  });

  const layoutOptions = useControls(
    {
      algorithm: {
        value: 'dagre' as LayoutOptions['algorithm'],
        options: [
          'dagre',
          'd3-hierarchy',
          'elk',
        ] as LayoutOptions['algorithm'][],
      },
      direction: {
        value: 'LR' as LayoutOptions['direction'],
        options: {
          'Left to Right': 'LR',
          'Top to Bottom': 'TB',
        } as Record<string, LayoutOptions['direction']>,
      },
      spacing: { value: [50, 50], step: 10 },
      'Apply Layout': button(() => {
        applyLayout(optionsRef.current);
      }),
    },
    { collapsed: false }
  );

  optionsRef.current = {
    algorithm: layoutOptions.algorithm as LayoutOptions['algorithm'],
    direction: layoutOptions.direction as LayoutOptions['direction'],
    spacing: layoutOptions.spacing as [number, number],
  };

  return <Leva hidden={!isVisible} titleBar={{ title: 'Auto Layout' }} />;
};
